/**
 * CRO-WEB-173 Onboarding 1st Page Design Experimentation
 *
 * Gets the experiment variant from PostHog feature flag.
 * This experiment tests different layouts of the welcome step in onboarding.
 */

import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useExperimentOverride } from "../useExperimentOverride";

/**
 * Hook to get the CRO-WEB-173 experiment variant
 * Returns the variant string or null if not assigned
 * Supports development overrides via localStorage
 */
export const useCroWeb173Variant = () => {
  const posthogVariant = useFeatureFlagVariantKey("cro-web-173");
  const { effectiveVariant, isInitialized } = useExperimentOverride(
    "cro-web-173",
    posthogVariant
  );

  return { variant: effectiveVariant, isInitialized };
};

/**
 * Hook to get the CRO-WEB-173 experiment variant with dev controls
 * Returns variant and methods to override for development
 */
export const useCroWeb173VariantWithDevControls = () => {
  const posthogVariant = useFeatureFlagVariantKey("cro-web-173");
  const experimentOverride = useExperimentOverride(
    "cro-web-173",
    posthogVariant
  );

  return {
    variant: experimentOverride.effectiveVariant,
    setDevVariant: experimentOverride.setOverrideVariant,
    clearDevVariant: experimentOverride.clearOverrideVariant,
    hasDevOverride: experimentOverride.hasOverride,
    isInitialized: experimentOverride.isInitialized,
    posthogVariant: typeof posthogVariant === "string" ? posthogVariant : null,
  };
};

/**
 * Get variant from PostHog feature flag (client-side only)
 * Used for experiment tracking configuration
 */
export const getCroWeb173Variant = (): string | null => {
  if (typeof window === "undefined") return null;

  // Check for experiment override first
  const experimentOverride = localStorage.getItem("dev_variant_cro-web-173");
  if (experimentOverride) {
    return experimentOverride;
  }

  // For tracking purposes, we'll let the hook handle the actual PostHog call
  return null;
};
